import { worthSending, type Arrival } from "@joogoos/contracts";
import { api } from "./api.js";

/**
 * Where the person came from, remembered by the browser until there is somebody to attach it to (2026-09-21).
 *
 * Only the browser knows. The request that creates an account carries nothing useful — a magic link is clicked from a
 * mail app and Google's callback says Google — so the first page load writes down the one fact it has, and the first
 * load after signing in hands it over (`POST /v1/me/arrival`, which writes it once and only for a new account).
 *
 * What is kept is what a visit to a customer's website keeps (ADR 0035): the **host** and the `utm_*` tags. Never the
 * full referrer, whose path carries somebody's search terms, and never the rest of the query string. It is in
 * `localStorage` rather than a cookie because it is one line the browser tells us once and nothing needs it on a
 * request; a browser that refuses storage simply never reports one, which is a missing row and not an error.
 */
const KEY = "jg.arrival";

/** What this page can see about where its visitor came from. Empty when the browser had nothing to say. */
export function firstTouch(search: string, referrer: string): Arrival {
  const q = new URLSearchParams(search);
  const campaign: Record<string, string> = {};
  for (const k of ["source", "medium", "campaign", "term", "content"] as const) {
    const v = q.get(`utm_${k}`)?.trim();
    if (v) campaign[k] = v.slice(0, 64);
  }
  // `?from=` first: a link that carried a host along knows something this page's referrer no longer does — the hop
  // through joogoos.com, where the referrer becomes our own site and whatever sent them there is lost.
  let from = q.get("from")?.trim().slice(0, 128) || undefined;
  if (!from && referrer) { try { from = new URL(referrer).hostname || undefined; } catch { from = undefined; } }
  return { ...(from ? { from } : {}), ...(Object.keys(campaign).length ? { campaign } : {}) };
}

type Stash = Arrival & { sent?: boolean };

const read = (): Stash | null => {
  try { return JSON.parse(localStorage.getItem(KEY) ?? "null") as Stash | null; } catch { return null; }
};
const write = (s: Stash): void => {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* a browser that keeps nothing reports nothing */ }
};

/**
 * The first touch, written once per browser and never revised.
 *
 * First wins: somebody who arrives from Instagram, reads the site, comes back a day later and signs up then came from
 * Instagram. A second load must not overwrite that with its own referrer, which by then is us.
 */
export function noteArrival(search = location.search, referrer = document.referrer): void {
  if (read()) return;
  write(firstTouch(search, referrer));
}

/** Hand it over, once, now that there is an account to attach it to. Failure is silence: nothing here is worth a screen. */
export async function sendArrival(): Promise<void> {
  const stash = read();
  if (!stash || stash.sent || !worthSending(stash)) return;
  try {
    await api.arrived({ from: stash.from, campaign: stash.campaign });
    write({ ...stash, sent: true });
  } catch { /* the source of one sign-up is not worth a word to the person signing up */ }
}
